import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

export default function SlicerMachineForm({ machine, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    uid: '',
    name: '',
    model: '',
    serial: '',
    maxFilaments: 1,
    devMode: false,
    enabled: true,
    notes: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [machineModels, setMachineModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);

  // Fetch machine models on mount
  useEffect(() => {
    const fetchMachineModels = async () => {
      try {
        const res = await api.get('/auth/machine-models/by-vendor');
        const groups = res.data?.data || res.data || [];
        const models = groups.flatMap(g => g.models || []);
        setMachineModels(models);
      } catch (err) {
        console.error('Failed to fetch machine models:', err);
      }
    };
    fetchMachineModels();
  }, []);

  useEffect(() => {
    if (machine) {
      const modelId = typeof machine.model === 'object' ? machine.model?._id : machine.model;

      setFormData({
        uid: machine.uid || '',
        name: machine.name || '',
        model: modelId || '',
        serial: machine.serial || '',
        maxFilaments: machine.maxFilaments || 1,
        devMode: machine.devMode || false,
        enabled: machine.enabled ?? true,
        notes: machine.notes || '',
        isActive: machine.isActive ?? true
      });

      // Set selected model for display
      if (modelId && machineModels.length > 0) {
        const found = machineModels.find(m => m._id === modelId);
        setSelectedModel(found || null);
      }
    }
  }, [machine, machineModels]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const cleanedData = {
        uid: formData.uid,
        name: formData.name,
        model: formData.model,
        serial: formData.serial,
        maxFilaments: formData.maxFilaments,
        devMode: formData.devMode,
        enabled: formData.enabled,
        notes: formData.notes,
        isActive: formData.isActive
      };
      await onSave(cleanedData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save machine');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleModelSelect = (e) => {
    const modelId = e.target.value;
    setFormData({ ...formData, model: modelId });
    const found = machineModels.find(m => m._id === modelId);
    setSelectedModel(found || null);
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {machine ? 'Edit Machine' : 'Add Machine'}
          </h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="uid" className="block text-sm font-medium text-slate-700 mb-2">
                  UID * (max 12 chars)
                </label>
                <input
                  type="text"
                  id="uid"
                  name="uid"
                  value={formData.uid}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={12}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={100}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>
            </div>

            {/* Model Selection */}
            <div>
              <label htmlFor="model" className="block text-sm font-medium text-slate-700 mb-2">
                Machine Model *
              </label>
              <select
                id="model"
                name="model"
                value={formData.model}
                onChange={handleModelSelect}
                required
                disabled={loading}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
              >
                <option value="">Select model...</option>
                {machineModels.map(m => (
                  <option key={m._id} value={m._id}>
                    {m.fullName || m.name} {m.vendor ? `(${m.vendor})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Model info preview (read-only from model) */}
            {selectedModel && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-slate-700">
                <h4 className="font-semibold text-blue-800 mb-2">Model Specs</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {selectedModel.buildVolume && (
                    <div>
                      <span className="text-xs text-slate-500 block">Build Volume</span>
                      <span className="font-medium">{selectedModel.buildVolume.x} x {selectedModel.buildVolume.y} x {selectedModel.buildVolume.z} mm</span>
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-slate-500 block">Cost/Hour</span>
                    <span className="font-medium">{selectedModel.costPerHour} &euro;</span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 block">Hotends</span>
                    <span className="font-medium">{selectedModel.hotends || 1}</span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 block">MMS</span>
                    <span className="font-medium">{selectedModel.mmsSupport ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Machine-specific fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="serial" className="block text-sm font-medium text-slate-700 mb-2">
                  Serial Number
                </label>
                <input
                  type="text"
                  id="serial"
                  name="serial"
                  value={formData.serial}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="maxFilaments" className="block text-sm font-medium text-slate-700 mb-2">
                  Maximum Filaments *
                </label>
                <input
                  type="number"
                  id="maxFilaments"
                  name="maxFilaments"
                  value={formData.maxFilaments}
                  onChange={(e) => setFormData({ ...formData, maxFilaments: parseInt(e.target.value) || 1 })}
                  required
                  disabled={loading}
                  min={1}
                  step={1}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-2">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={loading}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 resize-none"
              />
            </div>

            {/* Toggles */}
            <div className="flex gap-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Enabled</span>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="devMode"
                  checked={formData.devMode}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Dev Mode</span>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Active</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
